import { useState, useEffect, useCallback } from 'react'
import { superadminDirectorRightsApi } from '@/services/api/director'
import type { DirectorRights } from '@/services/api/director'
import { extractErrorMessage } from '@/utils/errorHandler'

interface DirectorRightsEditorProps {
  directors: Array<{ id: string; label: string }>
}

const TOGGLES: Array<{ key: keyof DirectorRights; label: string }> = [
  { key: 'can_approve', label: 'Genehmigen' },
  { key: 'can_reject', label: 'Ablehnen' },
  { key: 'can_info_request', label: 'Rückfrage' },
  { key: 'can_request_documents', label: 'Dokumente anfordern' },
  { key: 'can_decide_payer', label: 'Zahler entscheiden (privat/schulisch)' },
  { key: 'can_manual_assign', label: 'Manuell zuweisen (Chef-Recht)' },
  { key: 'can_adjust_case', label: 'Fall anpassen / zurückstellen' },
  { key: 'can_delegate', label: 'An andere Direktoren delegieren' },
  { key: 'see_dashboards', label: 'Zahlen/Dashboards sehen' },
]

export function DirectorRightsEditor({ directors }: DirectorRightsEditorProps) {
  const [selected, setSelected] = useState<string | null>(directors[0]?.id ?? null)
  const [rights, setRights] = useState<DirectorRights | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async (directorId: string) => {
    setLoading(true)
    setError(null)
    setNotice(null)
    try {
      setRights(await superadminDirectorRightsApi.getRights(directorId))
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Rechte konnten nicht geladen werden' }))
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { if (selected) load(selected) }, [selected, load])

  const save = async (patch: Partial<DirectorRights>) => {
    if (!selected) return
    setNotice(null)
    try {
      setRights(await superadminDirectorRightsApi.updateRights(selected, patch))
      setNotice('Gespeichert.')
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Speichern fehlgeschlagen' }))
    }
  }

  if (directors.length === 0) {
    return <p className="text-sm text-gray-500">Keine Direktoren vorhanden — erstellen Sie zuerst einen Direktor-Benutzer.</p>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select
          value={selected ?? ''}
          onChange={e => setSelected(e.target.value)}
          className="rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
        >
          {directors.map(d => (
            <option key={d.id} value={d.id}>{d.label}</option>
          ))}
        </select>
        <select
          value={rights?.visibility_scope ?? 'faculty'}
          onChange={e => save({ visibility_scope: e.target.value as 'faculty' | 'school' })}
          className="rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
        >
          <option value="faculty">Sichtbarkeit: eigene Fakultät</option>
          <option value="school">Sichtbarkeit: ganze Schule</option>
        </select>
      </div>

      {error && <div className="p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}
      {notice && <div className="p-3 bg-green-100 text-green-700 rounded text-sm">{notice}</div>}

      {loading && <p className="text-sm text-gray-500">Laden...</p>}
      {rights && !loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {TOGGLES.map(t => (
            <label key={String(t.key)} className="flex items-center gap-2 p-2 border border-gray-200 rounded-md">
              <input
                type="checkbox"
                checked={Boolean(rights[t.key])}
                onChange={e => save({ [t.key]: e.target.checked } as Partial<DirectorRights>)}
                className="w-4 h-4"
              />
              <span className="text-sm">{t.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
